import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api, ApiError, getStaffToken } from "../api/client";
import type { Reservation, ReservationSource, ReservationStatus, Seating } from "../api/types";
import Logo from "../components/Logo";
import LangSwitch from "../components/LangSwitch";
import ThemeToggle from "../components/ThemeToggle";
import { useLanguage } from "../i18n/LanguageContext";
import type { StringKey } from "../i18n/strings";
import { isoDate, parseIsoDate } from "../lib/hours";
import BusinessEditor from "./BusinessEditor";
import MenuEditor from "./MenuEditor";
import "./DashboardPage.css";

/**
 * The reservation book — staff only, not linked from the public nav.
 *
 * One shared password, one list. Website requests arrive as "requested" so
 * the team sees what still needs a confirmation call; WhatsApp and phone
 * bookings are typed in here and start out confirmed.
 */

const SOURCE_LABEL: Record<ReservationSource, StringKey> = {
  website: "srcWebsite",
  whatsapp: "srcWhatsapp",
  phone: "srcPhone",
};

const STATUS_LABEL: Record<ReservationStatus, StringKey> = {
  requested: "statusRequested",
  confirmed: "statusConfirmed",
  seated: "statusSeated",
  cancelled: "statusCancelled",
  no_show: "statusNoShow",
};

const SEATING_LABEL: Record<Seating, StringKey> = {
  any: "seatingAny",
  inside: "seatingInside",
  outside: "seatingOutside",
};

type Range = "today" | "tomorrow" | "week" | "day" | "month";

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

type Tab = "reservations" | "menu" | "business";

export default function DashboardPage() {
  const [authed, setAuthed] = useState(() => Boolean(getStaffToken()));
  const [tab, setTab] = useState<Tab>("reservations");
  return (
    <div className="dash">
      {authed ? (
        <Shell tab={tab} onTab={setTab} onLogout={() => setAuthed(false)}>
          {tab === "reservations" && <Book onLogout={() => setAuthed(false)} />}
          {tab === "menu" && <MenuEditor />}
          {tab === "business" && <BusinessEditor />}
        </Shell>
      ) : (
        <Login onLogin={() => setAuthed(true)} />
      )}
    </div>
  );
}

/** The bar shared by all tabs: brand, tab switch, language, theme, sign out. */
function Shell({ tab, onTab, onLogout, children }: { tab: Tab; onTab: (t: Tab) => void; onLogout: () => void; children: ReactNode }) {
  const { t } = useLanguage();
  return (
    <>
      <header className="dash-bar">
        <div className="dash-bar-inner">
          <Link to="/" className="dash-brand">
            <Logo height={26} />
          </Link>
          <nav className="dash-tabs" aria-label="Dashboard">
            <button className={`dash-tab${tab === "reservations" ? " is-active" : ""}`} onClick={() => onTab("reservations")}>
              {t("tabReservations")}
            </button>
            <button className={`dash-tab${tab === "menu" ? " is-active" : ""}`} onClick={() => onTab("menu")}>
              {t("tabMenu")}
            </button>
            <button className={`dash-tab${tab === "business" ? " is-active" : ""}`} onClick={() => onTab("business")}>
              {t("tabBusiness")}
            </button>
          </nav>
          <div className="dash-bar-actions">
            <LangSwitch />
            <ThemeToggle />
            <button className="btn btn-ghost btn-sm" onClick={() => { api.staff.logout(); onLogout(); }}>
              {t("dashLogout")}
            </button>
          </div>
        </div>
      </header>
      {children}
    </>
  );
}

// --- login -------------------------------------------------------------------

function Login({ onLogin }: { onLogin: () => void }) {
  const { t } = useLanguage();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [wrong, setWrong] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setWrong(false);
    try {
      const ok = await api.staff.login(username.trim(), password);
      if (ok) onLogin();
      else setWrong(true);
    } catch {
      setWrong(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dash-login">
      <form className="dash-login-card" onSubmit={submit}>
        <Logo height={34} />
        <div>
          <p className="eyebrow">{t("dashLoginTitle")}</p>
          <h1 className="dash-login-title">{t("dashTitle")}</h1>
        </div>
        <div className="field">
          <label className="label" htmlFor="dash-user">{t("dashUsername")}</label>
          <input
            id="dash-user"
            className={`input${wrong ? " is-invalid" : ""}`}
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="dash-pw">{t("dashPassword")}</label>
          <input
            id="dash-pw"
            className={`input${wrong ? " is-invalid" : ""}`}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {wrong && <span className="error-text">{t("dashWrongPassword")}</span>}
        </div>
        <button className="btn btn-primary" type="submit" disabled={busy || !password || !username.trim()}>
          {t("dashLoginCta")}
        </button>
        <Link className="tiny link" to="/">← {t("brand")}</Link>
      </form>
    </div>
  );
}

// --- the book ----------------------------------------------------------------

function Book({ onLogout }: { onLogout: () => void }) {
  const { t, locale } = useLanguage();
  const today = useMemo(() => new Date(), []);
  // /dashboard?date=YYYY-MM-DD opens that day — the e-mail notification links here.
  const [params] = useSearchParams();
  const linkedDate = params.get("date");
  const linked = linkedDate && parseIsoDate(linkedDate) ? linkedDate : null;

  const [range, setRange] = useState<Range>(linked ? "day" : "today");
  const [day, setDay] = useState(linked ?? isoDate(today));
  const [month, setMonth] = useState(isoDate(today).slice(0, 7)); // "YYYY-MM"
  const [source, setSource] = useState<ReservationSource | "all">("all");
  const [showCancelled, setShowCancelled] = useState(false);
  const [rows, setRows] = useState<Reservation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [from, to] = useMemo(() => {
    if (range === "today") return [isoDate(today), isoDate(today)];
    if (range === "tomorrow") return [isoDate(addDays(today, 1)), isoDate(addDays(today, 1))];
    if (range === "week") return [isoDate(today), isoDate(addDays(today, 6))];
    if (range === "month") {
      const [y, m] = month.split("-").map(Number);
      return [isoDate(new Date(y, m - 1, 1)), isoDate(new Date(y, m, 0))];
    }
    return [day, day];
  }, [range, day, month, today]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api.staff.reservations(from, to));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        api.staff.logout();
        onLogout();
        return;
      }
      setError(t("dashLoadError"));
    }
  }, [from, to, onLogout, t]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const visible = useMemo(
    () =>
      (rows ?? []).filter(
        (r) => (source === "all" || r.source === source) && (showCancelled || (r.status !== "cancelled" && r.status !== "no_show")),
      ),
    [rows, source, showCancelled],
  );

  const stats = useMemo(() => {
    const live = visible.filter((r) => r.status !== "cancelled" && r.status !== "no_show");
    return {
      count: live.length,
      guests: live.reduce((sum, r) => sum + r.guests, 0),
      open: visible.filter((r) => r.status === "requested").length,
    };
  }, [visible]);

  const byDate = useMemo(() => {
    const map = new Map<string, Reservation[]>();
    for (const r of visible) map.set(r.date, [...(map.get(r.date) ?? []), r]);
    // Rows added from the dialog land at the end; the book reads in time order.
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, list]) => [date, [...list].sort((a, b) => a.time.localeCompare(b.time) || a.id - b.id)] as const);
  }, [visible]);

  const patch = async (row: Reservation, change: Parameters<typeof api.staff.update>[1]) => {
    const updated = await api.staff.update(row.id, change);
    setRows((current) => current?.map((r) => (r.id === row.id ? updated : r)) ?? null);
  };

  const remove = async (row: Reservation) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.staff.remove(row.id);
    setRows((current) => current?.filter((r) => r.id !== row.id) ?? null);
  };

  const dateLabel = (iso: string) => {
    const d = parseIsoDate(iso);
    return d ? d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" }) : iso;
  };

  return (
    <>

      <main className="dash-main">
        <div className="dash-head">
          <div className="dash-bar-title">
            <strong>{t("dashTitle")}</strong>
            <span className="tiny">{t("dashSub")}</span>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            + {t("newReservation")}
          </button>
        </div>

        <div className="dash-controls">
          <div className="dash-chips" role="group" aria-label={t("date")}>
            {(["today", "tomorrow", "week", "month"] as const).map((key) => (
              <button key={key} className={`chip${range === key ? " is-active" : ""}`} onClick={() => setRange(key)}>
                {t(key === "today" ? "dashToday" : key === "tomorrow" ? "dashTomorrow" : key === "week" ? "dashWeek" : "dashMonth")}
              </button>
            ))}
            <input
              className={`input dash-date${range === "month" ? " is-active" : ""}`}
              type="month"
              value={month}
              onChange={(e) => { if (e.target.value) { setMonth(e.target.value); setRange("month"); } }}
              aria-label={t("dashMonth")}
            />
            <input
              className={`input dash-date${range === "day" ? " is-active" : ""}`}
              type="date"
              value={day}
              onChange={(e) => { setDay(e.target.value); setRange("day"); }}
              aria-label={t("date")}
            />
          </div>

          <div className="dash-chips" role="group" aria-label={t("source")}>
            {(["all", "website", "whatsapp", "phone"] as const).map((key) => (
              <button key={key} className={`chip${source === key ? " is-active" : ""}`} onClick={() => setSource(key)}>
                {key !== "all" && <SourceIcon source={key} />}
                {key === "all" ? t("dashAll") : t(SOURCE_LABEL[key])}
              </button>
            ))}
          </div>

          <label className="dash-toggle">
            <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
            {t("showCancelled")}
          </label>
        </div>

        <div className="dash-stats">
          <Stat label={t("stReservations")} value={stats.count} />
          <Stat label={t("stGuests")} value={stats.guests} />
          <Stat label={t("stOpen")} value={stats.open} tone={stats.open > 0 ? "bordeaux" : undefined} />
        </div>

        {stats.open > 0 && <p className="dash-hint">{t("dashHint")}</p>}

        {error && (
          <div className="dash-empty">
            <p>{error}</p>
            <button className="btn btn-outline btn-sm" onClick={load}>{t("retry")}</button>
          </div>
        )}

        {rows && !error && byDate.length === 0 && <p className="dash-empty">{t("dashEmpty")}</p>}

        {byDate.map(([date, list]) => (
          <section key={date} className="dash-day">
            <h2 className="dash-day-title">
              {dateLabel(date)}
              <span className="tiny nums"> · {list.length} · {list.reduce((s, r) => s + r.guests, 0)} {t("stGuests")}</span>
            </h2>
            <ul className="dash-list">
              <AnimatePresence initial={false}>
                {list.map((row) => (
                  <motion.li
                    key={row.id}
                    layout
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`res res-${row.status}`}
                  >
                    <span className="res-time nums">{row.time}</span>
                    <span className="res-guests nums" title={t("guests")}>{row.guests}</span>
                    <span className="res-main">
                      <span className="res-name">{row.name}</span>
                      <span className="res-meta tiny">
                        {row.phone && <a href={`tel:${row.phone.replace(/\s/g, "")}`} className="res-phone nums">{row.phone}</a>}
                        {row.seating && row.seating !== "any" && <span className="res-seating">· {t(SEATING_LABEL[row.seating])}</span>}
                        {row.occasion && row.occasion !== "occasionNone" && <span>· {t(row.occasion as StringKey)}</span>}
                        {row.notes && <span className="res-notes">· „{row.notes}“</span>}
                      </span>
                    </span>
                    <span className={`res-source src-${row.source}`} title={t(SOURCE_LABEL[row.source])}>
                      <SourceIcon source={row.source} />
                      <span>{t(SOURCE_LABEL[row.source])}</span>
                    </span>
                    <span className={`pill status-${row.status}`}>{t(STATUS_LABEL[row.status])}</span>
                    <span className="res-actions">
                      {row.status === "requested" && (
                        <button className="btn btn-primary btn-sm" onClick={() => patch(row, { status: "confirmed" })}>{t("actConfirm")}</button>
                      )}
                      {row.status === "confirmed" && (
                        <button className="btn btn-outline btn-sm" onClick={() => patch(row, { status: "seated" })}>{t("actSeated")}</button>
                      )}
                      {(row.status === "requested" || row.status === "confirmed") && (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => patch(row, { status: "cancelled" })}>{t("actCancel")}</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => patch(row, { status: "no_show" })}>{t("actNoShow")}</button>
                        </>
                      )}
                      {(row.status === "cancelled" || row.status === "no_show" || row.status === "seated") && (
                        <button className="btn btn-ghost btn-sm" onClick={() => patch(row, { status: "confirmed" })}>{t("actRestore")}</button>
                      )}
                      <button className="btn btn-ghost btn-sm res-delete" onClick={() => remove(row)} aria-label={t("actDelete")}>✕</button>
                    </span>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          </section>
        ))}
      </main>

      <AnimatePresence>
        {creating && (
          <NewReservation
            onClose={() => setCreating(false)}
            onCreated={(row) => {
              setCreating(false);
              // Show it if it lands in the current window, otherwise jump there.
              if (row.date >= from && row.date <= to) setRows((current) => [...(current ?? []), row]);
              else { setDay(row.date); setRange("day"); }
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "bordeaux" }) {
  return (
    <div className={`dash-stat${tone ? ` dash-stat-${tone}` : ""}`}>
      <span className="dash-stat-value nums display">{value}</span>
      <span className="dash-stat-label">{label}</span>
    </div>
  );
}

// --- new reservation (WhatsApp / phone) -------------------------------------

function NewReservation({ onClose, onCreated }: { onClose: () => void; onCreated: (row: Reservation) => void }) {
  const { t, language } = useLanguage();
  const [source, setSource] = useState<ReservationSource>("whatsapp");
  const [date, setDate] = useState(isoDate(new Date()));
  const [time, setTime] = useState("19:00");
  const [guests, setGuests] = useState(2);
  const [seating, setSeating] = useState<Seating>("any");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [bad, setBad] = useState<string[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setBad([]);
    try {
      const row = await api.staff.create({ source, date, time, guests, seating, name, phone, email: "", occasion: "", notes, language });
      onCreated(row);
    } catch (err) {
      setBad(err instanceof ApiError && err.fields.length ? err.fields : ["name"]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div className="dash-backdrop" onClick={onClose} role="presentation" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.form
        className="dash-new"
        role="dialog"
        aria-modal="true"
        aria-label={t("newReservation")}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 className="dash-new-title">{t("newReservation")}</h2>

        <div className="field">
          <span className="label">{t("source")}</span>
          <div className="dash-chips">
            {(["whatsapp", "phone", "website"] as const).map((key) => (
              <button key={key} type="button" className={`chip${source === key ? " is-active" : ""}`} onClick={() => setSource(key)}>
                <SourceIcon source={key} />
                {t(SOURCE_LABEL[key])}
              </button>
            ))}
          </div>
        </div>

        <div className="dash-new-row">
          <div className="field">
            <label className="label" htmlFor="n-date">{t("date")}</label>
            <input id="n-date" className={`input${bad.includes("date") ? " is-invalid" : ""}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div className="field">
            <label className="label" htmlFor="n-time">{t("time")}</label>
            <input id="n-time" className={`input${bad.includes("time") ? " is-invalid" : ""}`} type="time" step={900} value={time} onChange={(e) => setTime(e.target.value)} required />
          </div>
          <div className="field">
            <span className="label">{t("guests")}</span>
            <div className="stepper">
              <button type="button" className="stepper-btn" onClick={() => setGuests((g) => Math.max(1, g - 1))} aria-label="−">−</button>
              <span className="stepper-value nums">{guests}</span>
              <button type="button" className="stepper-btn" onClick={() => setGuests((g) => Math.min(60, g + 1))} aria-label="+">+</button>
            </div>
          </div>
        </div>

        <div className="field">
          <span className="label">{t("seating")}</span>
          <div className="dash-chips">
            {(["any", "inside", "outside"] as const).map((key) => (
              <button key={key} type="button" className={`chip${seating === key ? " is-active" : ""}`} onClick={() => setSeating(key)}>
                {t(SEATING_LABEL[key])}
              </button>
            ))}
          </div>
        </div>

        <div className="dash-new-row">
          <div className="field">
            <label className="label" htmlFor="n-name">{t("name")}</label>
            <input id="n-name" className={`input${bad.includes("name") ? " is-invalid" : ""}`} value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          </div>
          <div className="field">
            <label className="label" htmlFor="n-phone">{t("phone")}</label>
            <input id="n-phone" className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="n-notes">{t("notes")}</label>
          <input id="n-notes" className="input" value={notes} maxLength={500} placeholder={t("notesPlaceholder")} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div className="dash-new-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>{t("cancel")}</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t("sending") : t("save")}</button>
        </div>
      </motion.form>
    </motion.div>
  );
}

function SourceIcon({ source }: { source: ReservationSource }) {
  if (source === "whatsapp") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 1.8a8.2 8.2 0 0 1 0 16.4 8.1 8.1 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 0 1 12 3.8zm-3.3 4.4c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.3s1 2.7 1.2 2.9c.1.2 2 3.1 4.9 4.3 2.4 1 2.9.8 3.4.7.5 0 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.5-.3l-1.9-.9c-.3-.1-.5-.2-.7.1l-.9 1.1c-.2.2-.3.2-.6.1a6.6 6.6 0 0 1-3.3-2.9c-.2-.4.2-.4.6-1.2.1-.2 0-.4 0-.5L10.4 9c-.2-.5-.5-.5-.7-.5h-1z" />
      </svg>
    );
  }
  if (source === "phone") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}
